import { H2Component } from '~/app/shared/components/elements/h2/h2.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { ProfileCompanyComponent } from '~/app/domains/profile/components/profile-company/profile-company.component';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';

import { ApplicationFacade } from '~/app/core/state/application/application-facade/application.facade';
import { AuthenticationFacade } from '~/app/core/state/authentication/authentication.facade';
import {
    CheckboxInputComponent,
    CheckboxInputValue,
} from '~/app/shared/components/forms/formElements/checkbox-input/checkbox-input.component';
import { ButtonComponent } from '~/app/shared/components/elements/buttons/button/button.component';
import { ModalRef } from '~/app/shared/services/modal/modal-ref';

import { ProfileProfessionalInfoComponent } from '../../components/profile-professional-info/profile-professional-info.component';

/**
 * Popin d'accueil (EF-3852) : présente au login le formulaire d'informations professionnelles
 * lorsque le profil est incomplet. Le formulaire réutilisé (`eb-profile-professional-info`) est
 * autonome (prefill / save / snackbar) : on écoute juste le succès de sauvegarde exposé par la facade
 * pour refermer la popin. Seule exception (EF-3921) : son titre, surchargé via `titleKey` pour dire
 * pourquoi on demande ces informations ; sa logique reste intacte.
 *
 * EF-3852, recette PO du 2026-10-01 : la popin porte aussi « Ma société ». Les deux cartes sont intégrées (`embedded`) :
 * sans marge ni bouton propres, sous un seul « Enregistrer » commun dans un pied fixe. Chaque carte enregistre ce qui
 * la concerne (`/members`, `/companies`) ; la popin se ferme quand tout ce qui était modifié est enregistré.
 */
@Component({
    selector: 'eb-modal-professional-info',
    templateUrl: './modal-professional-info.component.html',
    styleUrls: ['./modal-professional-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoPipe,
        ProfileProfessionalInfoComponent,
        ProfileCompanyComponent,
        CheckboxInputComponent,
        ButtonComponent,
        H2Component,
        LabelComponent,
    ],
})
export class ModalProfessionalInfoComponent {
    private readonly ref = inject(ModalRef);
    private readonly applicationFacade = inject(ApplicationFacade);
    private readonly authenticationFacade = inject(AuthenticationFacade);

    protected readonly hide = signal(false);

    private readonly memberForm = viewChild(ProfileProfessionalInfoComponent);
    private readonly companyForm = viewChild(ProfileCompanyComponent);

    /** Ce qui reste à enregistrer après le clic : la popin se ferme quand la liste est vide. */
    private readonly pending = signal<ReadonlySet<'member' | 'company'>>(new Set());

    /**
     * Actif s'il y a au moins une modification, et qu'aucune carte modifiée n'est invalide. Une carte non modifiée ne
     * bloque pas : on n'impose pas de compléter la société pour enregistrer son profil.
     */
    protected readonly canSave = computed(() => {
        const cards = [this.memberForm(), this.companyForm()].filter((card) => !!card);
        const changed = cards.filter((card) => card.hasUnsavedChanges());
        return changed.length > 0 && changed.every((card) => card.canSave()) && this.pending().size === 0;
    });

    constructor() {
        this.authenticationFacade.onUpdateMemberInfoSuccess()
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.markSaved('member'));
    }

    public save(): void {
        const member = this.memberForm();
        const company = this.companyForm();
        const toSave = new Set<'member' | 'company'>();
        if (member?.hasUnsavedChanges()) toSave.add('member');
        if (company?.hasUnsavedChanges()) toSave.add('company');
        if (toSave.size === 0) return;
        this.pending.set(toSave);
        if (toSave.has('member')) member?.saveChanges();
        // La société ouvre sa confirmation (données communes à tous les membres) ; elle émet `saved` une fois validée.
        if (toSave.has('company')) company?.saveChanges();
    }

    public onCompanySaved(): void {
        this.markSaved('company');
    }

    private markSaved(card: 'member' | 'company'): void {
        const remaining = new Set(this.pending());
        remaining.delete(card);
        this.pending.set(remaining);
        if (remaining.size === 0) {
            this.ref.close(undefined);
        }
    }

    public onHideChange(value: CheckboxInputValue): void {
        const hide = value ?? false;
        this.hide.set(hide);
        this.applicationFacade.setHideProfileIncompletePopin(hide);
    }
}
